import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertCircle, CheckCircle2, Sparkles, ChevronRight, Activity,
  AlertTriangle, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authApi = (method, path, body) => {
  const token = localStorage.getItem("cps_token");
  return axios({
    method, url: `${API}${path}`, data: body,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  }).then(r => r.data);
};

const SEVERITY_LBL = {
  1: { lbl: "Crítico", color: "#c47a7a", bg: "rgba(196,122,122,0.12)" },
  2: { lbl: "Importante", color: "var(--gold)", bg: "rgba(184,147,90,0.12)" },
  3: { lbl: "Recomendado", color: "var(--ink-3)", bg: "rgba(157,163,170,0.10)" },
};

const scoreColor = (s) => {
  if (s >= 80) return "var(--verde-2)";
  if (s >= 60) return "var(--gold)";
  if (s >= 30) return "#d4ac5a";
  return "#c47a7a";
};

/* =========================================================================
 * OBRA HEALTH CARD — Embedded inside ObraVisao
 * Mostra barra de saúde + lista de próximos passos + botão Concierge AI
 * ========================================================================= */
export function ObraHealthCard({ obra, onReload }) {
  const [report, setReport] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const load = useCallback(() => {
    authApi("get", `/obras/${obra.id}/health`).then(setReport).catch(() => {});
  }, [obra.id]);
  useEffect(() => { load(); }, [load]);

  const askConcierge = async () => {
    setAiLoading(true);
    setAiText(null);
    try {
      const r = await authApi("post", `/obras/${obra.id}/concierge-suggest`);
      setAiText(r.ai_text);
    } catch (e) {
      setAiText("Não foi possível obter recomendação no momento. " + (e.response?.data?.detail || ""));
    } finally {
      setAiLoading(false);
    }
  };

  if (!report) return null;
  const score = report.score;
  const faltando = report.items_faltando || [];
  const completos = report.items_completos || [];
  const visibleFaltando = showAll ? faltando : faltando.slice(0, 5);

  return (
    <div
      className="panel fade-up"
      style={{
        padding: 0, marginBottom: 24,
        border: `1px solid ${scoreColor(score)}40`,
        background: `linear-gradient(180deg, ${scoreColor(score)}08 0%, transparent 100%)`,
      }}
      data-testid="obra-health-card"
    >
      {/* Header com score */}
      <div style={{ padding: "20px 24px", borderBottom: "1px solid var(--line)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 240px" }}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>
              <Activity size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />
              Saúde da obra
            </div>
            <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 20, margin: 0 }}>
              {score >= 90 ? <>Tudo em <em>ordem</em></> : score >= 60 ? <>Precisando de <em>ajustes</em></> : <>Atenção <em>requerida</em></>}
            </h3>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 38, color: scoreColor(score), lineHeight: 1 }} data-testid="health-score">{score}%</div>
              <div style={{ fontSize: 10, color: "var(--ink-3)", letterSpacing: ".15em", textTransform: "uppercase", marginTop: 4 }}>
                {completos.length}/{report.total_items} itens
              </div>
            </div>
          </div>
        </div>

        {/* Barra de progresso */}
        <div style={{ marginTop: 14, height: 6, background: "var(--bg-3)", overflow: "hidden" }}>
          <div style={{
            width: `${score}%`, height: "100%",
            background: scoreColor(score),
            transition: "width .8s ease",
          }} />
        </div>
      </div>

      {/* Próximo passo destacado */}
      {report.proximo_passo && (
        <div style={{
          padding: "16px 24px",
          background: SEVERITY_LBL[report.proximo_passo.severity].bg,
          borderBottom: "1px solid var(--line)",
        }} data-testid="proximo-passo">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
            <AlertTriangle size={20} color={SEVERITY_LBL[report.proximo_passo.severity].color} style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, letterSpacing: ".2em", textTransform: "uppercase", color: SEVERITY_LBL[report.proximo_passo.severity].color, marginBottom: 6 }}>
                Próximo passo · {SEVERITY_LBL[report.proximo_passo.severity].lbl}
              </div>
              <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 17, marginBottom: 4 }}>
                {report.proximo_passo.label}
              </div>
              <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>
                {report.proximo_passo.hint}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lista completa de faltando */}
      {faltando.length > 0 && (
        <div style={{ padding: "16px 24px" }}>
          <div className="eyebrow" style={{ marginBottom: 10 }}>Itens pendentes ({faltando.length})</div>
          <div style={{ display: "grid", gap: 8 }}>
            {visibleFaltando.map((it, idx) => (
              <div key={it.key} data-testid={`item-faltando-${it.key}`} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 14px",
                background: idx === 0 && !report.proximo_passo ? SEVERITY_LBL[it.severity].bg : "var(--bg-3)",
                border: "1px solid var(--line)",
              }}>
                <AlertCircle size={14} color={SEVERITY_LBL[it.severity].color} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--ink-1)" }}>{it.label}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{it.hint}</div>
                </div>
                <span style={{ fontSize: 9, padding: "2px 8px", color: SEVERITY_LBL[it.severity].color, border: `1px solid ${SEVERITY_LBL[it.severity].color}40`, textTransform: "uppercase", letterSpacing: ".15em", flexShrink: 0 }}>
                  {SEVERITY_LBL[it.severity].lbl}
                </span>
              </div>
            ))}
          </div>
          {faltando.length > 5 && (
            <button
              type="button"
              onClick={() => setShowAll(!showAll)}
              data-testid="health-toggle-all"
              style={{ marginTop: 12, background: "transparent", border: "none", color: "var(--gold)", fontSize: 12, cursor: "pointer", fontStyle: "italic", display: "flex", alignItems: "center", gap: 6 }}
            >
              <ChevronDown size={13} style={{ transform: showAll ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
              {showAll ? `Ocultar ${faltando.length - 5} itens` : `Ver mais ${faltando.length - 5} itens`}
            </button>
          )}
        </div>
      )}

      {/* Concierge AI */}
      <div style={{ padding: "16px 24px", borderTop: "1px solid var(--line)", background: "rgba(184,147,90,0.04)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 280px" }}>
            <div className="eyebrow" style={{ marginBottom: 4 }}>
              <Sparkles size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />
              Concierge AI
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-2)" }}>
              Recomendação consultiva sobre os próximos passos desta obra.
            </div>
          </div>
          <button
            className="btn"
            onClick={askConcierge}
            disabled={aiLoading}
            data-testid="btn-concierge-suggest"
          >
            <Sparkles size={13} />
            {aiLoading ? "Pensando…" : aiText ? "Pedir nova sugestão" : "Sugerir próximo passo"}
          </button>
        </div>

        {aiText && (
          <div data-testid="ai-suggestion" style={{
            marginTop: 14, padding: "16px 18px",
            background: "linear-gradient(135deg, rgba(184,147,90,0.10) 0%, rgba(58,90,74,0.05) 100%)",
            border: "1px solid var(--gold-3)",
            fontSize: 14, lineHeight: 1.7, color: "var(--ink-1)",
            whiteSpace: "pre-wrap",
          }}>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 11, color: "var(--gold)", letterSpacing: ".25em", textTransform: "uppercase", marginBottom: 12 }}>
              Concierge sussurra…
            </div>
            {aiText}
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================================
 * OBRAS ATENÇÃO — Card no Dashboard
 * Top 5 obras com health score baixo
 * ========================================================================= */
export function ObrasAtencaoCard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    authApi("get", "/obras-health/summary").then(setData).catch(() => setData({ top_atencao: [], em_atencao: 0 }));
  }, []);

  if (!data) return null;
  const top = data.top_atencao || [];
  if (top.length === 0) {
    return (
      <div className="panel" style={{ padding: 18, marginBottom: 16, border: "1px solid rgba(58,90,74,0.3)" }} data-testid="obras-atencao-card">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <CheckCircle2 size={18} color="var(--verde-2)" />
          <div>
            <div className="eyebrow">Saúde geral</div>
            <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 16, color: "var(--verde-2)" }}>
              Todas as obras em ordem ({data.media_score}% saúde média)
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="panel" style={{ padding: 0, marginBottom: 16, border: "1px solid var(--gold-3)" }} data-testid="obras-atencao-card">
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--line)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div className="eyebrow"><AlertTriangle size={11} style={{ display: "inline", marginRight: 6, verticalAlign: "middle", color: "var(--gold)" }} />Obras precisando de atenção</div>
          <h3 style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 18, margin: "4px 0 0" }}>
            {data.em_atencao} de {data.total_obras} obras
          </h3>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 24, color: scoreColor(data.media_score) }}>
            {data.media_score}%
          </div>
          <div style={{ fontSize: 9, color: "var(--ink-3)", letterSpacing: ".2em", textTransform: "uppercase", marginTop: 2 }}>Saúde média</div>
        </div>
      </div>
      <div>
        {top.map((r, idx) => (
          <div
            key={r.obra_id}
            onClick={() => navigate(`/obras/${r.obra_id}`)}
            data-testid={`atencao-row-${r.obra_id}`}
            style={{
              padding: "12px 18px",
              borderBottom: idx < top.length - 1 ? "1px solid var(--line)" : "none",
              cursor: "pointer", display: "flex", justifyContent: "space-between",
              alignItems: "center", gap: 14, transition: "background .15s",
            }}
            onMouseEnter={e => e.currentTarget.style.background = "var(--bg-3)"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: "var(--ink-3)", letterSpacing: ".15em" }}>{r.obra_codigo}</div>
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 15, marginTop: 2 }}>{r.obra_nome}</div>
              {r.proximo_passo && (
                <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4, fontStyle: "italic" }}>
                  → {r.proximo_passo.label}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "'Fraunces',serif", fontStyle: "italic", fontSize: 22, color: scoreColor(r.score), lineHeight: 1 }}>{r.score}%</div>
                <div style={{ fontSize: 9, color: "var(--ink-3)", marginTop: 2, letterSpacing: ".15em", textTransform: "uppercase" }}>
                  {r.items_faltando.length} pendentes
                </div>
              </div>
              <ChevronRight size={16} color="var(--ink-3)" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
